/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { CheckCircleFilled } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton/Button";
import { Card, Typography, Image, Alert, Checkbox, App } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import CustomModal from "../../../components/CustomModal/CustomModal";
import FailedImage from "../../../assets/images/txn-failed.png";
import { buyDeal } from "../../../redux/slices/DealSlice";
import { useDispatch, useSelector } from "react-redux";
import { getBalanceByOrgId } from "../../../redux/slices/manageFundSlice";
import VerifiedIcon from "../../../assets/icons/verified-icon.svg";
import {
  generateRiskAgreement,
  sendAgreementESignRequest,
  getAgreementEsignStatus,
  getDocketInfo,
} from "../../../redux/slices/EsignSlice";
import { getUserLocation } from "../../../helpers/helpers";

const { Title, Text } = Typography;

const DEAL_SIGNING_STEPS = [
  "Sign Seller Rights Agreement",
  "Please redirect back to application once signature is done",
  "Accept Risk Disclosure Agreement",
  "Proceed with Deal Buy",
];

const getSellerRightsSignedStorageKey = (dealId) =>
  dealId ? `sellerRightsSigned:${dealId}` : null;

const getDealPurchaseCompletedStorageKey = (dealId) =>
  dealId ? `dealPurchaseCompleted:${dealId}` : null;

const readPersistedSellerRightsSigned = (dealId) => {
  const key = getSellerRightsSignedStorageKey(dealId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persistSellerRightsSigned = (dealId, signReferenceId) => {
  const key = getSellerRightsSignedStorageKey(dealId);
  if (!key) return;
  localStorage.setItem(
    key,
    JSON.stringify({
      signed: true,
      signReferenceId: signReferenceId || null,
      signedAt: new Date().toISOString(),
    }),
  );
};

const Agreement = () => {
  const { user } = useSelector((state) => state.auth || {});
  const { buyDealLoading, error } = useSelector((state) => state.deal || {});
  const { accBalance } = useSelector((state) => state.manageFunds || {});
  const {
    tripartyAgreeLoading,
    riskAgreementLoading,
    riskAgreement,
    agreeReqError,
  } = useSelector((state) => state.esign || {});
  const [preAgreeModalOpen, setPreAgreeModalOpen] = useState(false);
  const [esignLink, setEsignLink] = useState(null);
  const [isRiskAccepted, setIsRiskAccepted] = useState(false);
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [failedModalOpen, setFailedModalOpen] = useState(false);
  const [esignCompleted, setEsignCompleted] = useState(false);
  const [signReferenceId, setSignReferenceId] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const esignCompletedRef = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { dealInfo } = location.state || {};
  const { message } = App.useApp();

  // After purchase, browser Back must not reopen this step
  useEffect(() => {
    const dealId = dealInfo?.dealId;
    if (!dealId) {
      navigate("/live-deals", { replace: true });
      return;
    }
    const purchaseKey = getDealPurchaseCompletedStorageKey(dealId);
    if (purchaseKey && sessionStorage.getItem(purchaseKey)) {
      navigate("/live-deals", { replace: true });
    }
  }, [dealInfo?.dealId, navigate]);

  const markEsignCompleted = (referenceId) => {
    esignCompletedRef.current = true;
    setEsignCompleted(true);
    if (referenceId) {
      setSignReferenceId(referenceId);
    }
    persistSellerRightsSigned(dealInfo?.dealId, referenceId);
  };

  const syncAgreementSignStatus = async ({
    document_id,
    docket_id,
    showPendingError = false,
  } = {}) => {
    if (!dealInfo?.dealId) return false;

    setStatusCheckLoading(true);
    try {
      const res = await dispatch(
        getAgreementEsignStatus({
          type: "investor",
          dealId: dealInfo.dealId,
          ...(document_id && { document_id }),
          ...(docket_id && { docket_id }),
        }),
      );

      const response = res?.payload?.response || res?.payload;
      if (response?.status === "signed") {
        if (document_id && docket_id) {
          await dispatch(
            getDocketInfo({
              dealId: dealInfo.dealId,
              document_id,
              docket_id,
            }),
          );
        }
        markEsignCompleted(response.signReferenceId);
        localStorage.removeItem("tripartyAgreeData");
        return true;
      }

      if (showPendingError) {
        message.error(
          "The document has not been signed yet. Please complete the Seller Transfer Rights.",
        );
      }
      return false;
    } catch {
      if (showPendingError) {
        message.error(
          "Unable to verify agreement signature status. Please try again.",
        );
      }
      return false;
    } finally {
      setStatusCheckLoading(false);
    }
  };

  useEffect(() => {
    if (dealInfo?.dealId) {
      dispatch(generateRiskAgreement(dealInfo.dealId));
    }
  }, [dealInfo?.dealId, dispatch]);

  // Restore signed state on mount / revisit (localStorage + API)
  useEffect(() => {
    if (!dealInfo?.dealId) return;

    const persisted = readPersistedSellerRightsSigned(dealInfo.dealId);
    if (persisted?.signed) {
      esignCompletedRef.current = true;
      setEsignCompleted(true);
      if (persisted.signReferenceId) {
        setSignReferenceId(persisted.signReferenceId);
      }
    }

    syncAgreementSignStatus();
  }, [dealInfo?.dealId]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible" || esignCompletedRef.current) {
        return;
      }

      const storedData = localStorage.getItem("tripartyAgreeData");
      if (!storedData) {
        await syncAgreementSignStatus();
        return;
      }

      try {
        const { document_id, docket_id } = JSON.parse(storedData);
        await syncAgreementSignStatus({
          document_id,
          docket_id,
          showPendingError: true,
        });
      } catch {
        message.error("Unable to verify agreement signature status.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [dealInfo?.dealId]);

  const handleTripartyAgreement = async () => {
    if (esignCompleted || esignCompletedRef.current) return;

    let location = userLocation;

    if (!location) {
      setLocationLoading(true);
      const locationData = await getUserLocation();
      location = locationData?.city || "";
      setUserLocation(location);
      setLocationLoading(false);
    }
    const payload = {
      dealId: dealInfo.dealId,
      dealActualAmt: dealInfo.amountToPayNow?.toString(),
      dealDiscountAmt: dealInfo.expectedGrossProfit,
      location: location,
    };
    const agreementRes = await dispatch(sendAgreementESignRequest(payload));

    if (agreementRes.payload.invitation_link) {
      const data = agreementRes.payload;

      // Persist in localStorage
      localStorage.setItem(
        "tripartyAgreeData",
        JSON.stringify({
          document_id: data.document_id,
          docket_id: data.docket_id,
          dealId: dealInfo.dealId,
        }),
      );

      setEsignLink(agreementRes.payload.invitation_link);
      setPreAgreeModalOpen(true);
    }
  };

  const handleCompletePurchase = async () => {
    const amountToPay = Number(dealInfo.amountToPayNow || 0);
    const walletBalance = Number(accBalance?.amount || 0);

    if (amountToPay > walletBalance) {
      message.error(
        "Insufficient balance in wallet. Please add funds before completing purchase.",
      );
      return;
    }

    const payload = {
      dealId: dealInfo.dealId,
      signReferenceId,
    };

    const res = await dispatch(buyDeal(payload));

    if (res.payload.statusCode === 200) {
      const purchaseKey = getDealPurchaseCompletedStorageKey(dealInfo?.dealId);
      if (purchaseKey) {
        sessionStorage.setItem(purchaseKey, "1");
      }
      const signedKey = getSellerRightsSignedStorageKey(dealInfo?.dealId);
      if (signedKey) {
        localStorage.removeItem(signedKey);
      }

      // replace so browser Back does not return to Sign Agreement
      navigate("/success", {
        replace: true,
        state: { dealInfo, txnId: res.payload.response.txnId },
      });

      // Update user's balance
      const resBalance = await dispatch(getBalanceByOrgId(user?.orgId));
      if (resBalance.payload.amount) {
        const amount = resBalance.payload.amount;
        const updatedAmount = { ...user, amount: amount };
        localStorage.setItem("invUser", JSON.stringify(updatedAmount));
      }
    } else {
      setFailedModalOpen(true);
    }
  };

  const failedTxnContent = (
    <div className="txn-failed-modal-container">
      <Image
        src={FailedImage}
        alt="Txn-Failed"
        preview={false}
        style={{ margin: "0 0 20px -20px" }}
      />
      <Title level={4}>Transaction Failed</Title>
      <Text style={{ color: "#40566D" }}>{error}</Text>
    </div>
  );

  return (
    <div className="agreement-container">
      <div className="stepper-container">
        <CustomButton
          type="default"
          shape="round"
          style={{ color: "#58728D" }}
          className="order-summary-btn"
          icon={<CheckCircleFilled style={{ color: "#00874F" }} />}
          iconPosition="start"
        >
          1. Order Summary
        </CustomButton>

        <div className="stepper-line" />

        <CustomButton
          type="primary"
          shape="round"
          className="order-summary-btn"
        >
          2. Sign Agreement
        </CustomButton>
      </div>
      <div className="agreement-content-row">
        <div className="agreement-details-container">
          <Card style={{ backgroundColor: "#F8FAFC", margin: "40px 0" }}>
            <Title level={5}>
              Seller Transfer of Rights Agreement and Risk Disclosure Terms
            </Title>
          </Card>

          {(error || agreeReqError) && !esignCompleted && (
            <Alert
              style={{ marginBottom: 20 }}
              message={error || agreeReqError}
              type="error"
              showIcon
            />
          )}
          <CustomButton
            type="default"
            onClick={handleTripartyAgreement}
            iconPosition="end"
            loading={
              tripartyAgreeLoading || locationLoading || statusCheckLoading
            }
            disabled={esignCompleted}
            style={{ marginBottom: 20 }}
            icon={
              esignCompleted ? (
                <Image preview={false} src={VerifiedIcon} alt="signed" />
              ) : null
            }
            block
          >
            {esignCompleted
              ? "Seller Rights Agreement Signed"
              : "Click to Sign and Review the Agreement"}
          </CustomButton>
          <Checkbox
            checked={isRiskAccepted}
            disabled={!esignCompleted}
            onChange={(e) => setIsRiskAccepted(e.target.checked)}
          >
            By checking this box, I confirm that I have read, understood and
            agreed to be bound by the{" "}
            <span
              style={{
                color: esignCompleted ? "#2243B6" : "#A0A0A0",
                textDecoration: esignCompleted ? "underline" : "none",
                cursor: esignCompleted ? "pointer" : "not-allowed",
              }}
              onClick={() => {
                if (esignCompleted) {
                  setIsRiskModalOpen(true);
                }
              }}
            >
              Risk Disclosure
            </span>
          </Checkbox>
          <CustomButton
            type="primary"
            onClick={handleCompletePurchase}
            style={{ marginTop: 20 }}
            className="purchase-button"
            loading={buyDealLoading}
            disabled={!esignCompleted || !isRiskAccepted}
            block
          >
            Complete Purchase
          </CustomButton>
          <CustomModal
            open={preAgreeModalOpen}
            centered
            onCancel={() => setPreAgreeModalOpen(false)}
            content={
              <>
                <Title level={5}>
                  Complete Seller Transfer Of Right Agreement
                </Title>
                <Text>Please click "Proceed" to complete your e-sign.</Text>
                <CustomButton
                  type="primary"
                  onClick={() => {
                    window.open(esignLink, "_blank");
                    setPreAgreeModalOpen(false);
                  }}
                  style={{ marginTop: 20 }}
                  block
                >
                  Proceed
                </CustomButton>
              </>
            }
          />
          <CustomModal
            open={isRiskModalOpen}
            centered
            width="70%"
            loading={riskAgreementLoading}
            onCancel={() => setIsRiskModalOpen(false)}
            content={
              <iframe
                title="Risk Disclosure"
                src={riskAgreement?.docUrl}
                width="100%"
                height="500px"
                style={{ border: "none" }}
              />
            }
          />
          <CustomModal
            open={failedModalOpen}
            centered
            onCancel={() => setFailedModalOpen(false)}
            content={failedTxnContent}
          />
        </div>

        <div className="deal-signing-steps-container">
          <Title level={5} style={{ color: "#0D1A48", marginBottom: 20 }}>
            How to Complete Deal Signing
          </Title>
          <div className="deal-signing-steps-list">
            {DEAL_SIGNING_STEPS.map((stepText, index) => (
              <div className="deal-signing-step-row" key={index}>
                <div className="deal-signing-step-indicator">
                  <div className="step">{index + 1}</div>
                  {index < DEAL_SIGNING_STEPS.length - 1 && (
                    <div className="steps-divider" />
                  )}
                </div>
                <Text className="deal-signing-step-text">{stepText}</Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agreement;
